import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import { CommentIndicatorOverlay } from './CommentIndicatorOverlay';
import { CommentsOptions, IndicatorType } from './types';
import { TimeoutId } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';

type Options = Pick<
  CommentsOptions,
  'blockThreadTypeName' | 'inlineThreadTypeName' | 'provider'
> & {
  offset: number;
};

type AggregatedThreadType = {
  node: Node;
  pos: number;
  threadIds: Set<string>;
};

// Helper function to aggregate threads by block
function aggregateThreadsByBlock(
  doc: Node,
  options: Options
): Map<string, AggregatedThreadType> {
  const blockThreads = new Map();
  const resolvedThreadById = new Map(
    (options.provider?.getThreads() ?? []).map(({ id, resolvedAt }) => [
      id,
      resolvedAt,
    ])
  );

  doc.descendants((node: Node, pos: number) => {
    if (node.type.name === options.blockThreadTypeName) {
      const id = node.attrs.id || node.attrs['data-block-thread-id'];

      if (id && !resolvedThreadById.get(id)) {
        const $pos = doc.resolve(pos + 1);
        const blockNode = $pos.node(1);
        const blockPos = $pos.before(1);

        if (!blockThreads.has(blockPos)) {
          blockThreads.set(blockPos, {
            node: blockNode,
            pos: blockPos,
            threadIds: new Set(),
          });
        }

        const blockData = blockThreads.get(blockPos);
        blockData.threadIds.add(id);
      }
    }

    node.marks.forEach((mark) => {
      if (mark.type.name === options.inlineThreadTypeName) {
        const id = mark.attrs.id || mark.attrs['data-inline-thread-id'];

        if (id && !resolvedThreadById.get(id)) {
          const $pos = doc.resolve(pos);
          const blockNode = $pos.node(1);
          const blockPos = $pos.before(1);

          if (!blockThreads.has(blockPos)) {
            blockThreads.set(blockPos, {
              node: blockNode,
              pos: blockPos,
              threadIds: new Set(),
            });
          }

          const blockData = blockThreads.get(blockPos);
          blockData.threadIds.add(id);
        }
      }
    });

    return true;
  });

  return blockThreads;
}

function getUnresolvedThreadsWithComments(
  options: Options
): Map<string, Set<string> | null> {
  return new Map(
    (options.provider?.getThreads() ?? []).map(
      ({ id, resolvedAt, comments }) => [
        id,
        resolvedAt || !comments?.length
          ? null
          : new Set(comments.map(({ id }) => id)),
      ]
    )
  );
}

export const CommentIndicator = Extension.create({
  name: 'commentIndicator',

  addOptions() {
    return {
      provider: null,
      blockThreadTypeName: 'blockThread',
      inlineThreadTypeName: 'inlineThread',
      onClick: undefined,
    };
  },

  addProseMirrorPlugins() {
    const { options } = this;

    return [
      new Plugin({
        key: new PluginKey('commentIndicator'),

        view: (editorView) => {
          let overlay: ReactRenderer | null = null;
          let updateTimeout: TimeoutId | null = null;
          let resizeObserver: ResizeObserver | null = null;

          // Create the overlay - view is guaranteed to be ready
          const createOverlay = () => {
            const editorElement = editorView.dom;
            const editorParent = editorElement.parentElement;

            if (!editorParent) {
              console.warn(
                'CommentIndicator: No parent element found for editor'
              );
              return;
            }

            editorParent.style.position = 'relative';

            overlay = new ReactRenderer(CommentIndicatorOverlay, {
              props: {
                indicators: [],
                onClick: options.onClick,
                offset: options.offset,
              },
              editor: this.editor,
            });

            editorParent.appendChild(overlay.element);

            Object.assign((overlay.element as HTMLElement).style, {
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: '10',
            });
          };

          // Update indicators
          const updateIndicators = () => {
            if (!overlay || !editorView) return;

            const blockThreads = aggregateThreadsByBlock(
              editorView.state.doc,
              options
            );
            const threadsWithComments =
              getUnresolvedThreadsWithComments(options);
            const indicators = [] as IndicatorType[];
            const editorRect = editorView.dom.getBoundingClientRect();

            blockThreads.forEach(({ pos, threadIds }) => {
              const count = threadIds.size;

              if (count > 0) {
                try {
                  const coords = editorView.coordsAtPos(pos);
                  const relativeTop =
                    coords.top - editorRect.top + editorView.dom.scrollTop;
                  const countWithComments = [...threadIds].reduce(
                    (acc: number, id: string) => {
                      const commentIds = threadsWithComments.get(id);
                      if (commentIds instanceof Set) {
                        return acc + commentIds.size;
                      }
                      return acc;
                    },
                    0
                  );

                  if (!countWithComments) return;

                  indicators.push({
                    pos,
                    count: countWithComments,
                    threadIds: [...threadIds],
                    top: relativeTop,
                  });
                } catch (error) {
                  console.error(
                    `Failed to get position for block at ${pos}:`,
                    error
                  );
                }
              }
            });

            overlay.updateProps({
              indicators,
              onClick: options.onClick,
            });
          };

          // Debounced update
          const debouncedUpdate = () => {
            if (updateTimeout) {
              clearTimeout(updateTimeout);
            }

            updateTimeout = setTimeout(() => {
              updateIndicators();
            }, 100);
          };

          createOverlay();
          updateIndicators();

          // Setup resize observer
          resizeObserver = new ResizeObserver(debouncedUpdate);
          resizeObserver.observe(editorView.dom);

          return {
            update: (view, prevState) => {
              // Called on every state update
              debouncedUpdate();
            },

            destroy: () => {
              // Cleanup
              if (overlay) {
                overlay.destroy();
                overlay = null;
              }

              if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
              }

              if (updateTimeout) {
                clearTimeout(updateTimeout);
              }
            },
          };
        },
      }),
    ];
  },
});
